import { AudioMutedOutlined, AudioOutlined } from '@ant-design/icons';
import { Button, Input, Tooltip } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './SpeechAntdTextArea.css';

export type SpeechAntdTextAreaHandle = {
  flush: () => Promise<string>;
  isRecognizing: () => boolean;
};

interface AllProps {
  autoFocus?: boolean;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
  value?: string;
  onChange?: (value: string) => void;
  showCount?: boolean;
}

const isBrowserSpeechRecognitionSupported = () => {
  const isEdge = navigator.userAgent.includes('Edg/');
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  if (isEdge) {
    return {
      isSupported: false,
      showDisabledButton: true,
      unsupportedMessage:
        'Speech dictation is currently supported in Chrome. Please use Chrome, or record/upload audio instead.'
    };
  }

  if (!SpeechRecognition) {
    return {
      isSupported: false,
      showDisabledButton: false,
      unsupportedMessage: 'Speech dictation is unavailable in this browser.'
    };
  }

  return {
    isSupported: true,
    showDisabledButton: false,
    unsupportedMessage: ''
  };
};

const SpeechAntdTextArea = forwardRef<SpeechAntdTextAreaHandle, AllProps>(
  ({ autoFocus, rows, placeholder, maxLength, value, onChange, showCount }, ref) => {
    const [isSupported, setIsSupported] = useState(true);
    const [showDisabledButton, setShowDisabledButton] = useState(false);
    const [recognizing, setRecognizing] = useState(false);

    const [message, setMessage] = useState('Click the microphone icon and start speaking.');

    const [finalTranscript, setFinalTranscript] = useState<string>(value ?? '');
    const [interimTranscript, setInterimTranscript] = useState<string>('');

    const recognitionRef = useRef<SpeechRecognition | null>(null);
    const finalRef = useRef<string>(value ?? '');

    const flushResolverRef = useRef<((text: string) => void) | null>(null);

    useEffect(() => {
      if (typeof value === 'string' && value !== finalRef.current) {
        finalRef.current = value;
        setFinalTranscript(value);
      }
    }, [value]);

    useEffect(() => {
      const support = isBrowserSpeechRecognitionSupported();

      if (!support.isSupported) {
        setIsSupported(false);
        setShowDisabledButton(support.showDisabledButton);
        setMessage(support.unsupportedMessage);
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

      const recognition: SpeechRecognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setRecognizing(true);
        setMessage('Listening… Speak now.');
      };

      recognition.onend = () => {
        setRecognizing(false);
        setInterimTranscript('');
        setMessage('Click the microphone to start again.');

        onChange?.(finalRef.current);

        if (flushResolverRef.current) {
          flushResolverRef.current(finalRef.current);
          flushResolverRef.current = null;
        }
      };

      recognition.onerror = (event: any) => {
        setRecognizing(false);

        if (flushResolverRef.current) {
          flushResolverRef.current(finalRef.current);
          flushResolverRef.current = null;
        }

        switch (event.error) {
          case 'no-speech':
            setMessage('No speech detected. Try again.');
            break;
          case 'audio-capture':
            setMessage('No microphone found.');
            break;
          case 'not-allowed':
            setMessage('Microphone permission denied.');
            break;
          default:
            setMessage(`Error: ${event.error}`);
        }
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let interim = '';
        let final = finalRef.current;

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const text = result[0].transcript;

          if (result.isFinal) {
            final = final ? final + ' ' + text : text;
          } else {
            interim += text;
          }
        }

        finalRef.current = final;
        setFinalTranscript(final);
        setInterimTranscript(interim);
      };

      recognitionRef.current = recognition;

      return () => {
        recognition.onstart = null;
        recognition.onend = null;
        recognition.onerror = null;
        recognition.onresult = null;
        recognition.stop();
      };
    }, [onChange]);

    useImperativeHandle(
      ref,
      () => ({
        flush: () => {
          if (!recognizing) {
            return Promise.resolve(finalRef.current);
          }

          if (flushResolverRef.current) {
            return new Promise<string>((resolve) => {
              const prev = flushResolverRef.current!;
              flushResolverRef.current = (text) => {
                prev(text);
                resolve(text);
              };
            });
          }

          return new Promise<string>((resolve) => {
            flushResolverRef.current = resolve;
            recognitionRef.current?.stop();
          });
        },
        isRecognizing: () => recognizing
      }),
      [recognizing]
    );

    const handleToggle = () => {
      const recognition = recognitionRef.current;
      if (!recognition) return;

      if (recognizing) {
        recognition.stop();
      } else {
        setMessage('Allow microphone access if prompted.');
        try {
          recognition.start();
        } catch {
          setRecognizing(false);
          setMessage('Speech dictation is unavailable right now. Please try again.');
        }
      }
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      finalRef.current = val;
      setFinalTranscript(val);
      setInterimTranscript('');
      onChange?.(val);
    };

    return (
      <div className="speech-antd-text-area">
        <Input.TextArea
          autoFocus={autoFocus}
          rows={rows ?? 6}
          maxLength={maxLength}
          showCount={showCount}
          value={interimTranscript ? finalTranscript + ' ' + interimTranscript : finalTranscript}
          onChange={handleTextChange}
          placeholder={placeholder ?? 'Start speaking and text will appear here…'}
        />

        {isSupported && (
          <Tooltip title={message}>
            <Button
              type={recognizing ? 'primary' : 'default'}
              danger={recognizing}
              icon={recognizing ? <AudioMutedOutlined /> : <AudioOutlined />}
              onClick={handleToggle}
              className="microphone-button"
            />
          </Tooltip>
        )}

        {!isSupported && showDisabledButton && (
          <Tooltip title={message}>
            <span>
              <Button type="default" disabled icon={<AudioOutlined />} className="microphone-button" />
            </span>
          </Tooltip>
        )}
      </div>
    );
  }
);

export default SpeechAntdTextArea;
